import { z } from 'zod';
import { approvalDecisionSchema, approvalTypeSchema } from '../../common/schemas/enum.schemas.js';
import { uuidSchema } from '../../common/schemas/id.schemas.js';
import { amountSchema, revisionSchema } from '../../common/schemas/money.schemas.js';
import { ownerSchema } from '../../common/schemas/owner.schemas.js';
/**
 * The one approval in the flow: **buying above the calculated ceiling** (plan §3.1, §5.6).
 *
 * It gates the *purchase*, never the offer — a salesperson may record whatever number they discussed
 * with the customer, but the money only moves once an administrator has said so. Three properties make
 * that promise real rather than decorative:
 *
 *  - It is **bound to a fingerprint**: the exact valuation state it was granted for (plan §5.2). Change a
 *    fact, a severity, an override, a cost item or the offer and the approval reports `isStale` — it is
 *    never deleted, because the request and the decision are part of the record.
 *  - It carries **explicit limits**: `purchaseMax` defaults to the offer that was asked for and an
 *    administrator may only lower it.
 *  - It is **single use**: the purchase consumes it in the same transaction that creates the vehicle, so
 *    one approval can never authorise two purchases.
 */
export const approvalLimitsSchema = z.object({
    purchaseMax: amountSchema.nullable(),
});
/**
 * What was asked for. The two money fields describe an `ABOVE_CEILING` request; the three correction
 * fields describe a `CORRECTION` on a sold vehicle (plan §5.6). They live in one object rather than in a
 * union because an approval row is read by one query and printed by one component, and a union would
 * make every consumer narrow a shape it already knows from `type`.
 */
export const approvalRequestSchema = z.object({
    ctaOffer: amountSchema.nullable(),
    ceiling: z.number().nullable(),
    /** `CORRECTION` only: which column or cost-item amount was corrected. */
    field: z.string().optional(),
    oldValue: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
    newValue: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
    /** `CORRECTION` on `costItemActual` only: the cost item the amount belongs to. */
    costItemId: uuidSchema.optional(),
});
export const approvalSchema = z.object({
    id: uuidSchema,
    owner: ownerSchema,
    type: approvalTypeSchema,
    status: approvalDecisionSchema,
    revision: revisionSchema,
    valuationId: uuidSchema.nullable(),
    fingerprint: z.string(),
    /** True when the record has moved on since the approval was bound to it (plan §5.6). */
    isStale: z.boolean(),
    limits: approvalLimitsSchema,
    request: approvalRequestSchema,
    reason: z.string().nullable(),
    requestedBy: uuidSchema.nullable(),
    requestedByName: z.string().nullable(),
    requestedAt: z.string().datetime(),
    decidedBy: uuidSchema.nullable(),
    decidedByName: z.string().nullable(),
    decidedAt: z.string().datetime().nullable(),
    /** Set once the purchase has spent it; a consumed approval authorises nothing further. */
    consumedAt: z.string().datetime().nullable(),
});
/** What an estimate detail carries, so the offer step can draw the badge without a second round trip. */
export const approvalSummarySchema = z.object({
    id: uuidSchema,
    status: approvalDecisionSchema,
    isStale: z.boolean(),
    purchaseMax: amountSchema.nullable(),
    requestedAt: z.string().datetime(),
    decidedAt: z.string().datetime().nullable(),
});
/** A pending request as the administrator's queue lists it. */
export const pendingApprovalRowSchema = approvalSchema.extend({
    estimateNo: z.string().nullable(),
    customerName: z.string().nullable(),
    make: z.string().nullable(),
    model: z.string().nullable(),
    modelYear: z.number().int().nullable(),
});
export const listPendingApprovalsOutputSchema = z.object({
    items: z.array(pendingApprovalRowSchema),
});
export const listApprovalsByOwnerInputSchema = z.object({
    owner: ownerSchema,
});
export const listApprovalsByOwnerOutputSchema = z.object({
    owner: ownerSchema,
    items: z.array(approvalSchema),
});
export const requestApprovalInputSchema = z.object({
    estimateId: uuidSchema,
    /** Why the offer is worth more than the calculation says; shown to the administrator deciding it. */
    reason: z.string().trim().max(2_000).optional(),
});
export const decideApprovalInputSchema = z.object({
    approvalId: uuidSchema,
    expectedRevision: revisionSchema,
    decision: z.enum(['APPROVED', 'REJECTED']),
    /** Defaults to the offer that was requested, and may only be **lowered** (plan §5.6). */
    purchaseMax: amountSchema.optional(),
    reason: z.string().trim().max(2_000).optional(),
});
//# sourceMappingURL=approvals.schemas.js.map